/**
 * Saved lobby settings, and how a saved copy ages.
 *
 * A preset is a snapshot of a `LobbySettings` object taken on one build and
 * replayed on another. Between those two moments the settings page moves: a
 * setting is added, an option is renamed, a mode stops existing. Replaying such
 * a snapshot silently would quietly hand the host a game configured by a
 * version of the app that no longer exists, which is worse than telling them.
 *
 * So a preset carries the site's version label as a human-readable stamp, and
 * the compatibility check below compares the snapshot against the settings
 * schema this build actually has. The label is what a person reads; the key
 * comparison is what decides whether the preset still fits.
 */
import { DEFAULT_SETTINGS } from "./types.js";
import { normalizeSettings } from "./lobbySettings.js";
export const MAX_PRESET_NAME_LENGTH = 60;
export const MAX_PRESET_DESCRIPTION_LENGTH = 280;
export const MIN_PRESET_RATING = 1;
export const MAX_PRESET_RATING = 5;
/** Every setting this build knows about, in a stable order. */
export const SETTINGS_KEYS = Object.keys(DEFAULT_SETTINGS).sort();
const COMPATIBLE = { status: 'ok', missing: [], unknown: [], invalid: [] };
/**
 * Compares a stored settings snapshot against this build's settings schema.
 *
 * Deliberately structural rather than a version-string comparison. Most
 * releases do not touch the settings page at all, and a preset saved three
 * versions ago that still lines up key for key is not in conflict with
 * anything — flagging it red would train people to ignore the flag.
 */
export function describePresetCompat(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { status: 'conflict', missing: [...SETTINGS_KEYS], unknown: [], invalid: [] };
    }
    const stored = raw;
    const normalized = normalizeSettings(stored, DEFAULT_SETTINGS);
    const missing = [];
    const invalid = [];
    for (const key of SETTINGS_KEYS) {
        if (!Object.prototype.hasOwnProperty.call(stored, key)) {
            missing.push(key);
            continue;
        }
        // A value that does not survive normalisation is one this build no longer
        // offers: a removed mode, a bracket size that is gone, a renamed option.
        if (!Object.is(stored[key], normalized[key]))
            invalid.push(key);
    }
    const known = new Set(SETTINGS_KEYS);
    const unknown = Object.keys(stored).filter((key) => !known.has(key));
    if (missing.length === 0 && unknown.length === 0 && invalid.length === 0)
        return COMPATIBLE;
    return { status: 'conflict', missing, unknown, invalid };
}
/** A one-line explanation of a conflict, for the badge's tooltip. */
export function describeCompatReason(compat) {
    if (compat.status === 'ok')
        return 'Matches this version of the settings page.';
    const parts = [];
    if (compat.missing.length)
        parts.push(`${compat.missing.length} newer setting${compat.missing.length === 1 ? '' : 's'} missing`);
    if (compat.invalid.length)
        parts.push(`${compat.invalid.length} saved value${compat.invalid.length === 1 ? '' : 's'} no longer offered`);
    if (compat.unknown.length)
        parts.push(`${compat.unknown.length} setting${compat.unknown.length === 1 ? '' : 's'} no longer exist${compat.unknown.length === 1 ? 's' : ''}`);
    return `${parts.join(', ')}. Applying it fills the gaps with the current defaults.`;
}
/**
 * Turns a stored snapshot into settings this build can use.
 *
 * Missing settings come from `base` — the lobby's current settings when one is
 * being applied — rather than from the compiled defaults, so applying a preset
 * saved before a setting existed leaves that setting where the host had it.
 */
export function reconcilePresetSettings(raw, base = DEFAULT_SETTINGS) {
    return normalizeSettings(raw, base);
}
export function normalizePresetName(value) {
    return typeof value === 'string' ? value.trim().slice(0, MAX_PRESET_NAME_LENGTH) : '';
}
export function normalizePresetDescription(value) {
    return typeof value === 'string' ? value.trim().slice(0, MAX_PRESET_DESCRIPTION_LENGTH) : '';
}
export function isValidPresetRating(value) {
    return (typeof value === 'number' &&
        Number.isInteger(value) &&
        value >= MIN_PRESET_RATING &&
        value <= MAX_PRESET_RATING);
}
//# sourceMappingURL=settingsPresets.js.map